import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Mail, MessageCircle } from 'lucide-react';
import api from '@/lib/apiClient';

const WHATSAPP_NUM = '5493765004360';
const WHATSAPP_MSG = 'Hola, quiero solicitar una propuesta a medida para LexOrbit.';
const EMAIL_CONTACTO = 'contacto@lexorbit.com.ar';

const plans = [
  {
    name: 'Abogado Independiente',
    price: 'USD 9.99',
    periodo: '/mes',
    desc: 'Para profesionales que trabajan solos y necesitan ordenar su gestión diaria.',
    features: ['Gestión de clientes y causas', 'Agenda y vencimientos', 'Vault de documentos', 'Asistente IA legal', 'Generador de escritos', 'Acceso individual', 'Reportes de facturación', 'Demo gratis 5 días'],
    cta: 'Probar gratis 5 días', highlighted: false, plan_id: 'independiente',
  },
  {
    name: 'Estudio hasta 3',
    price: 'USD 24.99',
    periodo: '/mes',
    desc: 'Para estudios chicos de hasta 3 abogados que trabajan en equipo.',
    features: ['Todo lo del plan individual', 'Hasta 3 abogados', 'Agenda compartida', 'Chat interno del equipo', 'Expedientes compartidos', 'Roles y permisos', 'Demo gratis 5 días'],
    cta: 'Probar gratis 5 días', highlighted: true, plan_id: 'estudio_3',
  },
  {
    name: 'Estudio hasta 5',
    price: 'USD 44.99',
    periodo: '/mes',
    desc: 'Para equipos que necesitan trabajar de forma coordinada.',
    features: ['Todo lo del plan anterior', 'Hasta 5 abogados', 'Agenda compartida', 'Chat interno del equipo', 'Expedientes compartidos', 'Roles y permisos', 'Reportes por abogado', 'Demo gratis 5 días'],
    cta: 'Probar gratis 5 días', highlighted: false, plan_id: 'estudio_5',
  },
  {
    name: 'Estudio hasta 10',
    price: 'USD 79.99',
    periodo: '/mes',
    desc: 'Para despachos de hasta 10 abogados.',
    features: ['Todo lo del plan anterior', 'Hasta 10 abogados', 'Dashboard de productividad', 'Importación de datos', 'Configuración avanzada', 'Soporte prioritario', 'Demo gratis 5 días'],
    cta: 'Probar gratis 5 días', highlighted: false, plan_id: 'estudio_10',
  },
  {
    name: 'A Medida',
    price: 'A convenir',
    periodo: '',
    desc: 'Para estudios que necesitan configuración y marca propias.',
    features: ['Todo lo de Estudio hasta 10', 'Página web pública propia', 'Portal de acceso para clientes', 'Personalización visual', 'Implementación asistida', 'Capacitación inicial', 'Módulos configurables', 'Soporte dedicado'],
    cta: 'Solicitar propuesta', highlighted: false, plan_id: 'custom',
  },
];

export default function PricingSection({ onSelectPlan }) {
  const [showPropuesta, setShowPropuesta] = useState(false);
  const [arsPorPlan, setArsPorPlan] = useState({});

  // Precio real en pesos (USD × dólar MEP del día). Público, sin login.
  useEffect(() => {
    api.get('/api/suscripcion/precios').then((r) => {
      const m = {};
      Object.entries(r.data?.planes || {}).forEach(([k, v]) => { if (v.precio_ars) m[k] = v.precio_ars; });
      setArsPorPlan(m);
    }).catch(() => {});
  }, []);

  const waLink = `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent(WHATSAPP_MSG)}`;
  const mailLink = `mailto:${EMAIL_CONTACTO}?subject=${encodeURIComponent('Propuesta a medida — LexOrbit')}&body=${encodeURIComponent(WHATSAPP_MSG)}`;

  const onCta = (planId) => {
    if (planId === 'custom') { setShowPropuesta(true); return; }
    onSelectPlan(planId);
  };

  return (
    <section id="planes" className="relative py-20 lg:py-28 bg-[#050505]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center mb-14">
          <span className="text-[#C9A44C] text-[11px] uppercase tracking-[0.2em] block mb-3">Planes</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-[#F8F6F0] tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Elegí el plan ideal para tu <span className="text-[#C9A44C] italic">forma de trabajar</span>
          </h2>
          <p className="text-[#B8B8B8] text-sm max-w-xl mx-auto leading-relaxed">
            Comenzá con una prueba demo de 5 días. Al finalizar, podés continuar con el plan que mejor se adapte a tu estudio jurídico.
          </p>
          <p className="text-[#B8B8B8]/50 text-[11px] mt-2">
            La demo gratuita no es un plan permanente. Permite probar el sistema durante 5 días antes de activar una suscripción.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {plans.map((p, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className={`relative flex flex-col rounded-xl p-6 transition-all duration-400 ${
                p.highlighted
                  ? 'bg-[#141414] border-2 border-[#C9A44C]/60 shadow-[0_0_40px_rgba(201,164,76,0.12)]'
                  : 'bg-[#141414] border border-[rgba(201,164,76,0.12)] hover:border-[rgba(201,164,76,0.3)]'
              }`}
              data-testid={`pricing-card-${i}`}>
              {p.highlighted && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="text-[#050505] text-[10px] px-4 py-1.5 rounded-full font-bold tracking-wider uppercase" style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)', boxShadow: '0 0 15px rgba(201,164,76,0.3)' }}>Recomendado</span>
                </div>
              )}
              {p.oferta && (
                <div className="absolute -top-3.5 right-3">
                  <span className="text-white text-[10px] px-3 py-1.5 rounded-full font-bold tracking-wider uppercase bg-red-600 shadow-[0_0_15px_rgba(220,38,38,0.4)]">{p.oferta}</span>
                </div>
              )}
              <h3 className="text-[#F8F6F0] text-base font-semibold mb-2 mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>{p.name}</h3>
              <p className="text-[#B8B8B8] text-[11px] leading-relaxed mb-4">{p.desc}</p>
              <div className="mb-4">
                {p.precioAnterior && (
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[#888] text-sm line-through">{p.precioAnterior}</span>
                    <span className="text-[9px] text-[#E0C878] font-semibold uppercase tracking-wide">Oferta de lanzamiento</span>
                  </div>
                )}
                <span className="text-[#C9A44C] text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>{p.price}</span>
                {p.periodo && <span className="text-[#888] text-xs ml-1">{p.periodo}</span>}
                {arsPorPlan[p.plan_id] && (
                  <div className="text-[#888] text-[11px] mt-1">≈ ${arsPorPlan[p.plan_id].toLocaleString('es-AR')} ARS/mes · al cambio del día</div>
                )}
              </div>
              <ul className="space-y-2 mb-6 flex-grow">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-[11px]">
                    <Check className="w-3.5 h-3.5 text-[#C9A44C] mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <span className="text-[#B8B8B8]">{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => onCta(p.plan_id)}
                className={`w-full py-3 rounded text-sm font-semibold transition-all duration-300 ${
                  p.highlighted
                    ? 'text-[#050505] hover:shadow-[0_0_25px_rgba(201,164,76,0.35)]'
                    : 'border border-[rgba(201,164,76,0.3)] text-[#F8F6F0] hover:border-[#C9A44C]/60 hover:bg-white/[0.03]'
                }`}
                style={p.highlighted ? { background: 'linear-gradient(135deg, #C9A44C, #E0C878)' } : {}}
                data-testid={`pricing-cta-${i}`}>
                {p.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal: solicitar propuesta a medida (email + WhatsApp) */}
      {showPropuesta && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && setShowPropuesta(false)}>
          <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.25)] rounded-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg text-[#F8F6F0]" style={{ fontFamily: 'Playfair Display, serif' }}>Solicitá tu propuesta a medida</h3>
              <button onClick={() => setShowPropuesta(false)} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#B8B8B8] text-sm leading-relaxed mb-5">
              Contanos qué necesita tu estudio (página pública, portal de clientes, marca propia, módulos a medida) y te armamos una propuesta personalizada. Sin compromiso.
            </p>
            <div className="space-y-3">
              <a href={waLink} target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2.5 py-3 rounded-lg font-semibold text-white text-sm transition-all hover:opacity-90"
                style={{ background: '#25D366' }}>
                <MessageCircle className="w-5 h-5" /> Escribinos por WhatsApp
              </a>
              <a href={mailLink}
                className="w-full flex items-center justify-center gap-2.5 py-3 rounded-lg font-semibold text-[#050505] text-sm transition-all hover:shadow-[0_0_25px_rgba(201,164,76,0.35)]"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                <Mail className="w-5 h-5" /> Enviar un email
              </a>
            </div>
            <p className="text-center text-[10px] text-[#666] mt-4">WhatsApp: +54 9 376 500-4360 · {EMAIL_CONTACTO}</p>
          </div>
        </div>
      )}
    </section>
  );
}
